/**
 * @deprecated Use RadixModal via `useRadix: true` in showModal() options.
 * This component is kept only for backward compatibility during the migration (TRK-5521).
 * Do not use HeadlessModal in new code.
 */
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/solid'
import classNames from 'classnames'
import { Collapse, Expand } from 'iconoir-react'
import { Fragment } from 'react'
import { twMerge } from 'tailwind-merge'
import { useConfirmation } from './ConfirmationContext'
import { useEscapeListener } from 'app/hooks/useEscapeListener'
import { minimizeModal, maximizeModal } from 'app/lib/modal'

const NOOP = () => {}

export interface ModalProps {
  open: boolean
  title?: string | React.ReactElement
  subtitle?: string
  isFullScreen?: boolean
  is80Percent?: boolean
  children?: React.ReactElement | undefined | null
  showActions?: boolean
  actionLabel?: string
  closable?: boolean
  titleClassName?: string
  className?: string
  hideTitle?: boolean
  /** Merged onto the dialog panel; lets a non-fullscreen modal constrain its width (the panel is min-w-full otherwise). Radix renderer only. */
  panelClassName?: string
  /** Merged onto the flex box that positions the panel in the viewport; lets a modal top-anchor instead of centering. Radix renderer only. */
  containerClassName?: string
  removePadding?: boolean
  overflowHidden?: boolean
  nestedModal?: React.ReactElement | undefined | null
  isMinimized?: boolean
  showMinimizeButton?: boolean
  isNested?: boolean
  extraActions?: React.ReactElement | undefined | null
  useRadix?: boolean
  onActionTriggered?: () => void
  onClose: () => void
}

/** @deprecated Use RadixModal via `useRadix: true` in showModal() options. See TRK-5521. */
export function HeadlessModal({
  open,
  title,
  subtitle,
  isFullScreen = true,
  is80Percent = false,
  children,
  onClose,
  showActions = false,
  actionLabel = `ok`,
  closable = true,
  titleClassName = ``,
  className,
  hideTitle = false,
  removePadding = false,
  overflowHidden = false,
  nestedModal,
  onActionTriggered = NOOP,
  isMinimized = false,
  showMinimizeButton = false,
  isNested = false,
  extraActions,
}: ModalProps): React.ReactElement {
  useEscapeListener(onClose, open)
  const [, isConfirmOpen] = useConfirmation()

  const handleClose = () => {
    if (isConfirmOpen) return
    onClose()
  }

  // For fullscreen modals, Dialog's onClose is a no-op because:
  // 1. Escape is handled by useEscapeListener (stack-based, only closes topmost)
  // 2. There's no backdrop to click outside of
  // This prevents cascading closes when multiple dialogs are open simultaneously.
  const dialogOnClose = isFullScreen ? () => {} : handleClose

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className={twMerge(`dialog relative z-40 px-6`, className)} onClose={dialogOnClose}>
        {!isFullScreen && !isMinimized && (
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-75"
            leave="ease-in duration-200"
            leaveFrom="opacity-75"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-(--scrim) transition-opacity" />
          </Transition.Child>
        )}
        <div
          className={classNames(
            `fixed z-10 flex justify-center`,
            // Top-anchor fullscreen modals so their header (and its close X) sits
            // at the visible top on mobile instead of being centered/clipped
            // above the fold; other modals stay vertically centered.
            isFullScreen ? `items-start` : `items-center`,
            isMinimized ? `bottom-3 left-3` : `inset-0`,
          )}
        >
          <div
            className={classNames(
              // Mobile: full width for these data-heavy modals so search + tables
              // have room; desktop keeps the 80% width via md:.
              { 'max-h-[80%] min-h-[80%] w-full items-stretch md:w-[80%]': is80Percent },
              // dvh (dynamic viewport), not vh: on mobile Safari 100vh is the
              // large viewport (taller than the visible area), which pushed the
              // fullscreen header off the top so the close X was unreachable.
              { 'h-dvh w-screen': isFullScreen },
              { 'items-end sm:items-center': !is80Percent },
              `flex justify-center text-center sm:p-0`,
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                data-surface="raised"
                className={classNames(
                  isFullScreen ? `sm:w-full` : `max-h-[90vh]`,
                  `flex min-w-full flex-1 transform flex-col items-stretch rounded-xl bg-surface-raised text-left shadow-xl transition-all`,
                  isFullScreen && `min-h-full`,
                  { 'overflow-hidden': overflowHidden },
                )}
              >
                {!hideTitle ? (
                  <div
                    className={classNames(
                      `flex h-20 shrink-0 items-center justify-between border-b border-default px-6 py-4`,
                      isMinimized && `rounded-b-lg`,
                    )}
                  >
                    <div>
                      <div
                        className={classNames(
                          `text-lg font-semibold leading-6 text-strong`,
                          isMinimized && `!text-base`,
                          titleClassName,
                        )}
                      >
                        {title}
                      </div>
                      {!!subtitle && <div className="text-sm capitalize text-subtle">{subtitle}</div>}
                    </div>
                    <div className="flex items-center gap-x-6">
                      {showMinimizeButton && !isNested && (
                        <button
                          aria-label={isMinimized ? `maximize` : `minimize`}
                          id="minimize-modal-btn"
                          onClick={() => (isMinimized ? maximizeModal() : minimizeModal())}
                          type="button"
                        >
                          {isMinimized ? (
                            <Expand className="h-5 w-5 cursor-pointer" />
                          ) : (
                            <Collapse className="h-5 w-5 cursor-pointer" />
                          )}
                        </button>
                      )}
                      {closable && (
                        <button aria-label="close" id="close-modal-btn" onClick={handleClose} type="button">
                          <XMarkIcon className="h-5 w-5 cursor-pointer" />
                        </button>
                      )}
                    </div>
                  </div>
                ) : null}

                <div
                  className={classNames(
                    `min-h-0 grow`,
                    { 'overflow-hidden': overflowHidden },
                    { 'overflow-y-auto': !overflowHidden },
                    { 'px-6 py-4': !removePadding },
                    isMinimized && `hidden`,
                  )}
                >
                  {children}
                </div>

                {showActions && (
                  <div className="flex items-center justify-between border-t border-default px-6 py-3">
                    <div>{extraActions}</div>
                    <div className="flex gap-2">
                      <button aria-label="Cancel" onClick={handleClose} className="btn btn-secondary btn-md">
                        Cancel
                      </button>
                      <button onClick={onActionTriggered} className="btn btn-primary btn-md">
                        {actionLabel}
                      </button>
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
        {nestedModal && (
          <div onKeyDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            {nestedModal}
          </div>
        )}
      </Dialog>
    </Transition.Root>
  )
}
